import React, { useState, useEffect, useCallback, useMemo, FC } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMobileNav } from '../contexts/MobileNavContext';
import {
  PlanOverviewResponse,
  TopUpProduct,
  PurchaseTopUpRequest,
  PurchaseTopUpResponse,
} from '@dreamai/types';
import { usePlanModal } from '../providers';
import { useAgent } from '../contexts/AgentContext';
import api from '../api';
import CancelPlanModal from './settings/CancelPlanModal';
import TopUpModal from './settings/TopUpModal';
import AutoTopupSection from './settings/AutoTopupSection';
import YourDetailsSection from './settings/YourDetailsSection';
import ConnectedAccountsSection from './settings/ConnectedAccountsSection';
import BillingDetailsSection from './settings/BillingDetailsSection';
import HumanTimeActivityModal from './settings/HumanTimeActivityModal';
import PurchasePotSection from './settings/PurchasePotSection';
import DailyStandupSection from './settings/DailyStandupSection';
import NotificationsSection from './settings/NotificationsSection';
import MobileNavToggle from './chat/MobileNavToggle';
import { ReactComponent as DreamIcon } from '../icons/dream-icon.svg';
import './settings/settings-v2.scss';

// ── Inline SVG icons (matching Figma) ──────────────────────────────────

const UserIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <g clipPath='url(#clip0_1162_9158)'>
      <path
        d='M3.33368 18.1805C3.83583 18.3327 4.51407 18.3327 5.66699 18.3327H14.3337C15.4866 18.3327 16.1648 18.3327 16.667 18.1805M3.33368 18.1805C3.22601 18.1479 3.12644 18.1083 3.03202 18.0602C2.56161 17.8205 2.17916 17.4381 1.93948 16.9677C1.66699 16.4329 1.66699 15.7328 1.66699 14.3327V5.66602C1.66699 4.26588 1.66699 3.56582 1.93948 3.03104C2.17916 2.56063 2.56161 2.17818 3.03202 1.9385C3.5668 1.66602 4.26686 1.66602 5.66699 1.66602H14.3337C15.7338 1.66602 16.4339 1.66602 16.9686 1.9385C17.439 2.17818 17.8215 2.56063 18.0612 3.03104C18.3337 3.56582 18.3337 4.26588 18.3337 5.66602V14.3327C18.3337 15.7328 18.3337 16.4329 18.0612 16.9677C17.8215 17.4381 17.439 17.8205 16.9686 18.0602C16.8742 18.1083 16.7746 18.1479 16.667 18.1805M3.33368 18.1805C3.33395 17.5061 3.338 17.1492 3.39771 16.849C3.66073 15.5267 4.69439 14.4931 6.01669 14.2301C6.33869 14.166 6.7259 14.166 7.50033 14.166H12.5003C13.2748 14.166 13.662 14.166 13.984 14.2301C15.3063 14.4931 16.3399 15.5267 16.6029 16.849C16.6627 17.1492 16.6667 17.5061 16.667 18.1805M13.3337 7.91602C13.3337 9.75696 11.8413 11.2493 10.0003 11.2493C8.15938 11.2493 6.66699 9.75696 6.66699 7.91602C6.66699 6.07507 8.15938 4.58268 10.0003 4.58268C11.8413 4.58268 13.3337 6.07507 13.3337 7.91602Z'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_1162_9158'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

const ShoppingBagIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M4.6 2.19935L3.3 3.93268C3.04251 4.276 2.91377 4.44765 2.91676 4.59134C2.91936 4.71638 2.97799 4.83364 3.07646 4.91074C3.18962 4.99935 3.40419 4.99935 3.83333 4.99935H16.1667C16.5958 4.99935 16.8104 4.99935 16.9235 4.91074C17.022 4.83364 17.0806 4.71638 17.0832 4.59134C17.0862 4.44765 16.9575 4.276 16.7 3.93268L15.4 2.19935M4.6 2.19935C4.74667 2.00379 4.82 1.90602 4.91294 1.8355C4.99525 1.77304 5.08846 1.72643 5.18782 1.69805C5.3 1.66602 5.42222 1.66602 5.66667 1.66602H14.3333C14.5778 1.66602 14.7 1.66602 14.8122 1.69805C14.9115 1.72643 15.0047 1.77304 15.0871 1.8355C15.18 1.90602 15.2533 2.00379 15.4 2.19935M4.6 2.19935L3.03333 4.28824C2.83545 4.55208 2.73651 4.684 2.66625 4.82928C2.6039 4.9582 2.55843 5.09461 2.53096 5.23515C2.5 5.39353 2.5 5.55843 2.5 5.88824L2.5 15.666C2.5 16.5994 2.5 17.0661 2.68166 17.4227C2.84144 17.7363 3.09641 17.9912 3.41002 18.151C3.76654 18.3327 4.23325 18.3327 5.16667 18.3327L14.8333 18.3327C15.7668 18.3327 16.2335 18.3327 16.59 18.151C16.9036 17.9912 17.1586 17.7363 17.3183 17.4227C17.5 17.0661 17.5 16.5994 17.5 15.666V5.88824C17.5 5.55843 17.5 5.39353 17.469 5.23515C17.4416 5.09461 17.3961 4.9582 17.3338 4.82929C17.2635 4.68401 17.1646 4.55208 16.9667 4.28824L15.4 2.19935M13.3333 8.33268C13.3333 9.21674 12.9821 10.0646 12.357 10.6897C11.7319 11.3148 10.8841 11.666 10 11.666C9.11594 11.666 8.2681 11.3148 7.64298 10.6897C7.01786 10.0646 6.66667 9.21674 6.66667 8.33268'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LinkIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M8.33301 10.8327C8.69088 11.3109 9.14747 11.7071 9.67196 11.9939C10.1965 12.2807 10.7762 12.4513 11.3719 12.4938C11.9676 12.5363 12.5651 12.4497 13.1239 12.2403C13.6826 12.0308 14.1889 11.7032 14.6088 11.2802L17.1088 8.78024C17.8636 7.99774 18.2793 6.94524 18.2693 5.85274C18.2593 4.76024 17.8244 3.71574 17.055 2.94724C16.2857 2.17874 15.2408 1.74474 14.1483 1.73574C13.0558 1.72674 12.0038 2.14324 11.2213 2.89857L9.78967 4.32191M11.6663 9.16607C11.3085 8.68791 10.8519 8.29171 10.3274 8.0049C9.80289 7.71808 9.22318 7.54751 8.62749 7.50499C8.0318 7.46246 7.43427 7.54907 6.87553 7.75853C6.31679 7.96799 5.81046 8.29559 5.39051 8.71857L2.89051 11.2186C2.13576 12.0011 1.72009 13.0536 1.73009 14.1461C1.7401 15.2386 2.175 16.2831 2.94434 17.0516C3.71368 17.8201 4.75867 18.2541 5.85117 18.2631C6.94367 18.2721 7.99559 17.8556 8.77801 17.1002L10.2013 15.6769'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CoinsIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M13.2814 13.2814C16.1336 12.8996 18.3333 10.4567 18.3333 7.5C18.3333 4.27834 15.7216 1.66667 12.5 1.66667C9.54328 1.66667 7.10037 3.8664 6.71852 6.71856M6.24996 10.8333L7.49996 10V14.5833M6.24996 14.5833H8.74996M13.3333 12.5C13.3333 15.7217 10.7216 18.3333 7.49996 18.3333C4.2783 18.3333 1.66663 15.7217 1.66663 12.5C1.66663 9.27834 4.2783 6.66667 7.49996 6.66667C10.7216 6.66667 13.3333 9.27834 13.3333 12.5Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CreditCardIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M18.3337 8.33268H1.66699M9.16699 11.666H5.00033M1.66699 6.83268L1.66699 13.166C1.66699 14.0994 1.66699 14.5661 1.84865 14.9227C2.00844 15.2363 2.2634 15.4912 2.57701 15.651C2.93353 15.8327 3.40024 15.8327 4.33366 15.8327L15.667 15.8327C16.6004 15.8327 17.0671 15.8327 17.4236 15.651C17.7372 15.4912 17.9922 15.2363 18.152 14.9227C18.3337 14.5661 18.3337 14.0994 18.3337 13.166V6.83268C18.3337 5.89926 18.3337 5.43255 18.152 5.07603C17.9922 4.76243 17.7372 4.50746 17.4236 4.34767C17.0671 4.16602 16.6004 4.16602 15.667 4.16602L4.33366 4.16602C3.40024 4.16602 2.93353 4.16602 2.57701 4.34767C2.2634 4.50746 2.00844 4.76243 1.84865 5.07603C1.66699 5.43255 1.66699 5.89926 1.66699 6.83268Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CertificateIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <path
      d='M9 18.5H15M7 15H17M5 2H19C20.1046 2 21 2.99492 21 4.22222V19.7778C21 21.0051 20.1046 22 19 22H5C3.89543 22 3 21.0051 3 19.7778V4.22222C3 2.99492 3.89543 2 5 2ZM11.9976 6.21194C11.2978 5.4328 10.1309 5.22321 9.25414 5.93667C8.37738 6.65013 8.25394 7.84299 8.94247 8.6868C9.631 9.53061 11.9976 11.5 11.9976 11.5C11.9976 11.5 14.3642 9.53061 15.0527 8.6868C15.7413 7.84299 15.6329 6.64262 14.7411 5.93667C13.8492 5.23072 12.6974 5.4328 11.9976 6.21194Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const PlusCircleIcon: FC = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#000d26'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <line x1='12' y1='8' x2='12' y2='16' />
    <line x1='8' y1='12' x2='16' y2='12' />
  </svg>
);

const BellIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M7.79508 17.5C8.38254 18.0187 9.15284 18.3333 9.99967 18.3333C10.8465 18.3333 11.6168 18.0187 12.2043 17.5M14.9997 6.66667C14.9997 5.34058 14.4729 4.06881 13.535 3.13113C12.5971 2.19345 11.3254 1.66667 9.99967 1.66667C8.67359 1.66667 7.40182 2.19345 6.46414 3.13113C5.52646 4.06881 4.99967 5.34058 4.99967 6.66667C4.99967 9.24182 4.34507 11.0052 3.62415 12.1713C3.0171 13.1529 2.71357 13.6437 2.72528 13.7756C2.73826 13.9213 2.7664 13.9718 2.88204 14.0622C2.98669 14.1443 3.48287 14.1667 4.47524 14.1667H15.5241C16.5165 14.1667 17.0127 14.1443 17.1173 14.0622C17.233 13.9718 17.2611 13.9213 17.2741 13.7756C17.2858 13.6437 16.9823 13.1529 16.3752 12.1713C15.6543 11.0052 14.9997 9.24182 14.9997 6.66667Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ChevronRightIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M6 4L10 8L6 12'
      stroke='#838B9B'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ArrowLeftIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M16.6668 10H3.3335M3.3335 10L10.0002 16.6667M3.3335 10L10.0002 3.33333'
      stroke='#000D26'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const AssistantCardIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <g clipPath='url(#clip0_2150_50902)'>
      <path
        d='M13.2815 13.2818C16.1337 12.8999 18.3334 10.457 18.3334 7.50033C18.3334 4.27866 15.7217 1.66699 12.5001 1.66699C9.5434 1.66699 7.10049 3.86672 6.71864 6.71888M6.25008 10.8337L7.50008 10.0003V14.5837M6.25008 14.5837H8.75008M13.3334 12.5003C13.3334 15.722 10.7217 18.3337 7.50008 18.3337C4.27842 18.3337 1.66675 15.722 1.66675 12.5003C1.66675 9.27866 4.27842 6.66699 7.50008 6.66699C10.7217 6.66699 13.3334 9.27866 13.3334 12.5003Z'
        stroke='#838B9B'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_2150_50902'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

// ── Helpers ─────────────────────────────────────────────────────────────

function formatMinutes(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0 mins';
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (mins > 0) parts.push(`${mins} min${mins !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

function formatHoursLabel(hours: number): { value: string; unit: string } {
  if (hours >= 24 && hours % 24 === 0) {
    const days = hours / 24;
    return { value: String(days), unit: days === 1 ? 'Day' : 'Days' };
  }
  return { value: String(hours), unit: hours === 1 ? 'Hour' : 'Hours' };
}

function formatPrice(amount: number, currency: string): string {
  const symbols: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };
  const sym = symbols[currency] || currency + ' ';
  const withTax = amount * 1.2;
  const rounded = Math.round(withTax);
  return `${sym}${rounded}`;
}

function formatResetDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// ── Component ──────────────────────────────────────────────────────────

type SettingsTab =
  | 'your-details'
  | 'connected-accounts'
  | 'plan-and-topups'
  | 'purchase-pot'
  | 'billing-details'
  | 'daily-standup'
  | 'notifications';

const SETTING_TO_TAB: Record<string, SettingsTab> = {
  'your-details': 'your-details',
  'connected-accounts': 'connected-accounts',
  'plan-and-topups': 'plan-and-topups',
  'purchase-pot': 'purchase-pot',
  'billing-details': 'billing-details',
  'daily-standup': 'daily-standup',
  notifications: 'notifications',
};

const MOBILE_NAV_ITEMS: {
  key: SettingsTab;
  label: string;
  icon: FC;
}[] = [
  { key: 'your-details', label: 'Your Details', icon: UserIcon },
  { key: 'connected-accounts', label: 'Connected Accounts', icon: LinkIcon },
  { key: 'plan-and-topups', label: 'Plan & Top-Ups', icon: ShoppingBagIcon },
  { key: 'purchase-pot', label: "Assistant's Card", icon: CoinsIcon },
  {
    key: 'billing-details',
    label: 'Billing & Invoices',
    icon: CreditCardIcon,
  },
  { key: 'daily-standup', label: 'Daily Rundown', icon: CertificateIcon },
  { key: 'notifications', label: 'Notifications', icon: BellIcon },
];

const MOBILE_TAB_LABELS: Record<string, string> = {
  'your-details': 'Your Details',
  'connected-accounts': 'Connected Accounts',
  'plan-and-topups': 'Plan & Top-Ups',
  'purchase-pot': "Assistant's Card",
  'billing-details': 'Billing & Invoices',
  'daily-standup': 'Daily Rundown',
  notifications: 'Notifications',
};

const Settings: React.FC = () => {
  const { setting } = useParams<{ setting?: string }>();
  const navigate = useNavigate();
  const { isMobile } = useMobileNav();
  const { getAccessTokenSilently } = useAuth0();
  const { openPlanModal } = usePlanModal();
  const { agents } = useAgent();
  const paAgent = agents.find(
    (a) => (a.agent_type as string) === 'Personal Assistant',
  );
  const hasPA = !!paAgent;
  const paName = paAgent?.nickname || 'Assistant';

  const assistantName = useMemo(() => {
    const pa = agents.find(
      (a) => (a.agent_type as string) === 'Personal Assistant',
    );
    return pa?.nickname || pa?.name || 'Assistant';
  }, [agents]);

  const getMobileLabel = useCallback(
    (key: string, fallback: string) => {
      if (key === 'purchase-pot') return `${assistantName}'s Card`;
      return fallback;
    },
    [assistantName],
  );

  const defaultTab: SettingsTab = hasPA ? 'plan-and-topups' : 'your-details';
  const initialTab = (setting && SETTING_TO_TAB[setting]) || defaultTab;
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  const [overview, setOverview] = useState<PlanOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingPlan, setCancellingPlan] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [purchasingTopUp, setPurchasingTopUp] = useState<string | null>(null);
  const [topUpSuccess, setTopUpSuccess] = useState<string | null>(null);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [topUpModalProduct, setTopUpModalProduct] =
    useState<TopUpProduct | null>(null);
  const [showHumanTimeActivity, setShowHumanTimeActivity] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await api.get<PlanOverviewResponse>(
        '/credits/plan-overview',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOverview(res.data);
    } catch (err) {
      console.error('Error fetching plan overview:', err);
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleCancelPlan = useCallback(async () => {
    if (cancellingPlan) return;
    setCancellingPlan(true);
    setCancelError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/credits/cancel-plan',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowCancelModal(false);
      await fetchOverview();
    } catch (err) {
      console.error('Error cancelling plan:', err);
      setCancelError('Something went wrong. Please try again.');
    } finally {
      setCancellingPlan(false);
    }
  }, [cancellingPlan, getAccessTokenSilently, fetchOverview]);

  const openTopUpModal = useCallback((topUp: TopUpProduct) => {
    setTopUpSuccess(null);
    setTopUpError(null);
    setTopUpModalProduct(topUp);
  }, []);

  const handleTopUpConfirm = useCallback(
    async (topUp: TopUpProduct, quantity: number) => {
      if (purchasingTopUp) return;
      setPurchasingTopUp(topUp.priceId);
      setTopUpError(null);

      try {
        const token = await getAccessTokenSilently();
        const body: PurchaseTopUpRequest = {
          priceId: topUp.priceId,
          quantity,
        };
        const res = await api.post<PurchaseTopUpResponse>(
          '/credits/purchase-topup',
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setTopUpModalProduct(null);
        setTopUpSuccess(
          `Added ${res.data.hoursAdded} hour${res.data.hoursAdded !== 1 ? 's' : ''} of ${res.data.creditType === 'ai_time' ? 'AI' : 'Human'} time.`,
        );
        await fetchOverview();
      } catch (err) {
        console.error('Error purchasing top-up:', err);
        setTopUpError('Failed to purchase top-up. Please try again.');
      } finally {
        setPurchasingTopUp(null);
      }
    },
    [purchasingTopUp, getAccessTokenSilently, fetchOverview],
  );

  // ── Render ──────────────────────────────────────────────────────────

  const renderPlanAndTopUps = () => {
    if (loading) {
      return <div className='settings-loading'>Loading...</div>;
    }

    if (error || !overview) {
      return (
        <div className='settings-error'>{error || 'Something went wrong.'}</div>
      );
    }

    const {
      hasSubscription,
      plan,
      credits,
      resetsAt,
      cancellingAt,
      topUpProducts,
    } = overview;

    // ── Plan section ──────────────────────────────────────────────────
    const ai = plan
      ? formatHoursLabel(plan.aiHours)
      : { value: '1', unit: 'Hour' };
    const human = plan
      ? formatHoursLabel(plan.humanHours)
      : { value: '0', unit: 'Hour' };

    const aiTopUp = topUpProducts.find((t) => t.creditType === 'ai_time');
    const humanTopUp = topUpProducts.find((t) => t.creditType === 'human_time');

    return (
      <>
        {/* ── Plan ──────────────────────────────────────────── */}
        <div className='settings-section'>
          <h2 className='section-title'>Plan</h2>
          <div className='plan-overview-card'>
            <div className='plan-left'>
              <div className='plan-info-block'>
                <h3
                  className={`plan-status-label ${hasSubscription ? 'active' : 'trial'}`}
                >
                  {hasSubscription ? plan?.name || 'Active' : 'Trial'}
                </h3>
                {plan?.description && (
                  <p className='plan-description'>{plan.description}</p>
                )}
                <p className='plan-price-line'>
                  {hasSubscription && plan ? (
                    <>
                      <span className='plan-price-amount'>
                        {formatPrice(plan.amount, plan.currency)}
                      </span>
                      <span className='plan-price-period'>
                        /{plan.periodUnit.toLowerCase()}
                      </span>
                    </>
                  ) : (
                    <span className='plan-price-amount'>Free</span>
                  )}
                </p>
              </div>

              <div className='plan-time-pills'>
                <div className='time-pill ai'>
                  <div className='pill-number'>{ai.value}</div>
                  <div className='pill-text'>
                    <span className='pill-unit'>{ai.unit}</span>
                    <span className='pill-label'>AI</span>
                  </div>
                </div>
                <span className='time-plus'>+</span>
                <div
                  className={`time-pill human${!hasSubscription ? ' faded' : ''}`}
                >
                  <div className='pill-number'>{human.value}</div>
                  <div className='pill-text'>
                    <span className='pill-unit'>{human.unit}</span>
                    <span className='pill-label'>Human</span>
                  </div>
                </div>
              </div>
            </div>

            <div className='plan-right'>
              <div className='remaining-allowance'>
                <p className='remaining-label'>Remaining allowance</p>
                <p className='remaining-value'>
                  <span className='remaining-bold'>
                    {formatMinutes(credits.aiTimeMinutes)}
                  </span>{' '}
                  AI time
                </p>
                {hasSubscription && (
                  <div className='remaining-value-row'>
                    <p className='remaining-value'>
                      <span className='remaining-bold'>
                        {formatMinutes(credits.humanTimeMinutes)}
                      </span>{' '}
                      human time
                    </p>
                    <button
                      className='ht-info-btn'
                      onClick={() => setShowHumanTimeActivity(true)}
                      aria-label='View Human Time activity'
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <circle
                          cx='8'
                          cy='8'
                          r='7'
                          stroke='currentColor'
                          strokeWidth='1.5'
                        />
                        <text
                          x='8'
                          y='12'
                          textAnchor='middle'
                          fill='currentColor'
                          fontSize='11'
                          fontWeight='600'
                          fontFamily='Geist, sans-serif'
                        >
                          ?
                        </text>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className='plan-actions-area'>
                <div className='plan-actions'>
                  <button
                    className='btn-plan-primary'
                    onClick={() => openPlanModal(plan?.priceId)}
                  >
                    Change plan
                  </button>
                  {hasSubscription && !cancellingAt && (
                    <button
                      className='btn-plan-secondary'
                      onClick={() => setShowCancelModal(true)}
                    >
                      Cancel plan
                    </button>
                  )}
                </div>

                {hasSubscription && cancellingAt && (
                  <p className='cancel-date'>
                    Your plan will cancel on {formatResetDate(cancellingAt)}
                  </p>
                )}
                {hasSubscription && !cancellingAt && resetsAt && (
                  <p className='reset-date'>
                    Your allowance resets on {formatResetDate(resetsAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Top-ups ────────────────────────────────────────── */}
        <div className='settings-section'>
          <div className='topups-header'>
            <h2 className='section-title'>Top-ups</h2>
            {hasSubscription && (
              <p className='topups-subtitle'>
                Your top-ups will roll over to following months and are used
                after your subscription time.
              </p>
            )}
          </div>

          {topUpSuccess && (
            <p className='topup-feedback success'>{topUpSuccess}</p>
          )}
          {topUpError && <p className='topup-feedback error'>{topUpError}</p>}

          {hasSubscription ? (
            <div className='topups-container'>
              {aiTopUp && (
                <div className='topup-pill ai'>
                  <div className='topup-pill-number'>
                    {aiTopUp.hoursPerUnit * 5}
                  </div>
                  <div className='topup-pill-text'>
                    <span className='topup-pill-unit'>Hours</span>
                    <span className='topup-pill-label'>AI</span>
                  </div>
                  <span className='topup-price'>
                    {formatPrice(aiTopUp.unitAmount * 5, aiTopUp.currency)}
                  </span>
                  <button
                    className='topup-add-btn'
                    onClick={() => openTopUpModal(aiTopUp)}
                    disabled={purchasingTopUp !== null}
                  >
                    <PlusCircleIcon />
                  </button>
                </div>
              )}
              {humanTopUp && (
                <div className='topup-pill human'>
                  <div className='topup-pill-number'>
                    {humanTopUp.hoursPerUnit}
                  </div>
                  <div className='topup-pill-text'>
                    <span className='topup-pill-unit'>
                      {humanTopUp.hoursPerUnit === 1 ? 'Hour' : 'Hours'}
                    </span>
                    <span className='topup-pill-label'>Human</span>
                  </div>
                  <span className='topup-price'>
                    {formatPrice(humanTopUp.unitAmount, humanTopUp.currency)}
                  </span>
                  <button
                    className='topup-add-btn'
                    onClick={() => openTopUpModal(humanTopUp)}
                    disabled={purchasingTopUp !== null}
                  >
                    <PlusCircleIcon />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className='topups-disabled'>
              <CertificateIcon />
              <p>You need to be on an active Plan to top up your account</p>
            </div>
          )}
        </div>

        {/* ── Auto top-up ──────────────────────────────────── */}
        <AutoTopupSection
          topUpProducts={topUpProducts}
          hasSubscription={hasSubscription}
        />
      </>
    );
  };

  // ── Mobile: determine active sub-page from URL param ────────────────
  const mobileSubPage = setting && SETTING_TO_TAB[setting] ? setting : null;

  const renderMobileContent = () => {
    if (!mobileSubPage) {
      // ── Mobile root menu ──────────────────────────────────────────
      return (
        <div className='settings-mobile-root'>
          <MobileNavToggle />
          <div className='settings-mobile-header'>
            <h1 className='settings-mobile-title'>Settings</h1>
          </div>
          <nav className='settings-mobile-nav'>
            {MOBILE_NAV_ITEMS.map(({ key, label, icon: Icon }) => {
              if (
                !hasPA &&
                (key === 'plan-and-topups' ||
                  key === 'billing-details' ||
                  key === 'purchase-pot')
              ) {
                return null;
              }
              return (
                <button
                  key={key}
                  className='settings-mobile-nav-item'
                  onClick={() => navigate(`/settings/${key}`)}
                >
                  <Icon />
                  <span className='settings-mobile-nav-label'>
                    {getMobileLabel(key, label)}
                  </span>
                  <ChevronRightIcon />
                </button>
              );
            })}
          </nav>
        </div>
      );
    }

    // ── Mobile sub-page ──────────────────────────────────────────────
    return (
      <div className='settings-mobile-subpage'>
        <div className='settings-mobile-back-header'>
          <button
            className='settings-mobile-back-btn'
            onClick={() => navigate('/settings')}
          >
            <ArrowLeftIcon />
            <span>Back to Settings</span>
          </button>
        </div>
        <div className='settings-mobile-subpage-title'>
          <h1>
            {getMobileLabel(
              mobileSubPage,
              MOBILE_TAB_LABELS[mobileSubPage] || 'Settings',
            )}
          </h1>
        </div>
        <div className='settings-mobile-subpage-content'>
          {mobileSubPage === 'plan-and-topups' && renderPlanAndTopUps()}
          {mobileSubPage === 'your-details' && <YourDetailsSection />}
          {mobileSubPage === 'connected-accounts' && (
            <ConnectedAccountsSection />
          )}
          {mobileSubPage === 'purchase-pot' && (
            <PurchasePotSection assistantName={paName} />
          )}
          {mobileSubPage === 'billing-details' && <BillingDetailsSection />}
          {mobileSubPage === 'daily-standup' && <DailyStandupSection />}
          {mobileSubPage === 'notifications' && <NotificationsSection />}
        </div>
      </div>
    );
  };

  return (
    <div className='settings-v2-wrapper inset-white-backed'>
      <div className='settings-top-bar'>
        <Link to='/' className='settings-top-bar-logo' aria-label='Home'>
          <DreamIcon />
        </Link>
        <Link to='/' className='settings-top-bar-link'>
          <ArrowLeftIcon />
          <span>Back to Home</span>
        </Link>
      </div>
      {isMobile ? (
        renderMobileContent()
      ) : (
        <div className='settings-v2'>
          <h1 className='settings-page-title'>Settings</h1>

          <div className='settings-layout'>
            <nav className='settings-nav'>
              <button
                className={`nav-item${activeTab === 'your-details' ? ' active' : ''}`}
                onClick={() => setActiveTab('your-details')}
              >
                <UserIcon />
                <span>Your details</span>
              </button>
              <button
                className={`nav-item${activeTab === 'connected-accounts' ? ' active' : ''}`}
                onClick={() => setActiveTab('connected-accounts')}
              >
                <LinkIcon />
                <span>Connected accounts</span>
              </button>
              {hasPA && (
                <button
                  className={`nav-item${activeTab === 'plan-and-topups' ? ' active' : ''}`}
                  onClick={() => setActiveTab('plan-and-topups')}
                >
                  <ShoppingBagIcon />
                  <span>Plan & Top-Ups</span>
                </button>
              )}
              {hasPA && (
                <button
                  className={`nav-item${activeTab === 'purchase-pot' ? ' active' : ''}`}
                  onClick={() => setActiveTab('purchase-pot')}
                >
                  <CoinsIcon />
                  <span>{paName}&rsquo;s Card</span>
                </button>
              )}
              {hasPA && (
                <button
                  className={`nav-item${activeTab === 'billing-details' ? ' active' : ''}`}
                  onClick={() => setActiveTab('billing-details')}
                >
                  <CreditCardIcon />
                  <span>Billing & Invoices</span>
                </button>
              )}
              <button
                className={`nav-item${activeTab === 'daily-standup' ? ' active' : ''}`}
                onClick={() => setActiveTab('daily-standup')}
              >
                <CertificateIcon />
                <span>Daily Rundown</span>
              </button>
              <button
                className={`nav-item${activeTab === 'notifications' ? ' active' : ''}`}
                onClick={() => setActiveTab('notifications')}
              >
                <BellIcon />
                <span>Notifications</span>
              </button>
            </nav>

            <main className='settings-content'>
              {activeTab === 'plan-and-topups' && renderPlanAndTopUps()}
              {activeTab === 'purchase-pot' && (
                <PurchasePotSection assistantName={paName} />
              )}
              {activeTab === 'your-details' && <YourDetailsSection />}
              {activeTab === 'connected-accounts' && (
                <ConnectedAccountsSection />
              )}
              {activeTab === 'billing-details' && <BillingDetailsSection />}
              {activeTab === 'daily-standup' && <DailyStandupSection />}
              {activeTab === 'notifications' && <NotificationsSection />}
            </main>
          </div>
        </div>
      )}

      <CancelPlanModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelError(null);
        }}
        onConfirm={handleCancelPlan}
        resetsAt={overview?.resetsAt ?? null}
        isSubmitting={cancellingPlan}
        error={cancelError}
      />

      <TopUpModal
        isOpen={topUpModalProduct !== null}
        onClose={() => {
          setTopUpModalProduct(null);
          setTopUpError(null);
        }}
        onConfirm={handleTopUpConfirm}
        topUp={topUpModalProduct}
        cardInfo={overview?.cardInfo ?? null}
        isSubmitting={purchasingTopUp !== null}
        error={topUpError}
      />

      <HumanTimeActivityModal
        isOpen={showHumanTimeActivity}
        onClose={() => setShowHumanTimeActivity(false)}
      />
    </div>
  );
};

export default Settings;
